import { useState, useCallback, useRef, useLayoutEffect, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigationType, useLocation as useRouterLocation } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useActivities } from '@hooks/useActivities';
import { useAuth } from '@context/AuthContext';
import { useLocation } from '@context/LocationContext';
import FaceVerificationModal from '@components/safety/FaceVerificationModal';
import JoinLoginPromptModal from '@components/auth/JoinLoginPromptModal';
import { useNearbyNotifications } from '@hooks/useNotifications';
import Header from '@components/layout/Header';
import LocationPicker from '@components/ui/LocationPicker';
import ActivityCard from '@components/activity/ActivityCard';
import PeopleLooking from '@components/activity/PeopleLooking';
import PostIntentButton from '@components/activity/PostIntentButton';
import ExploreFilterBar from '@components/feed/ExploreFilterBar';
import ExploreFilterSheet from '@components/feed/ExploreFilterSheet';
import ExploreSortSheet from '@components/feed/ExploreSortSheet';
import Spinner from '@components/ui/Spinner';
import EmptyState from '@components/ui/EmptyState';
import api from '@utils/api';
import { trackActivityJoin } from '@utils/analytics';
import toast from 'react-hot-toast';
import {
  loadPersistedExploreFilters,
  persistExploreFilters,
  matchesExploreFilters,
  countActiveExploreFilters,
  exploreFiltersToApiParams,
  cloneExploreFilters,
} from '@utils/exploreFilters';
import {
  loadPersistedExploreSort,
  persistExploreSort,
  sortExploreActivities,
} from '@utils/exploreSort';
import { isActivityListingEnded } from '@utils/helpers';
import { clearPendingJoinIntent, readPendingJoinIntent, setPendingJoinIntent } from '@utils/pendingJoinIntent';

const CATEGORY_FILTERS = [
  { id: '', label: 'All Vibes', emoji: '✨' },
  { id: 'sports', label: 'Sports', emoji: '🏅' },
  { id: 'fitness', label: 'Fitness', emoji: '💪' },
  { id: 'learning', label: 'Learning', emoji: '📚' },
  { id: 'social', label: 'Social', emoji: '🎉' },
  { id: 'outdoors', label: 'Outdoors', emoji: '🌿' },
  { id: 'gaming', label: 'Gaming', emoji: '🎮' },
  { id: 'food', label: 'Food', emoji: '🍳' },
];

const FEED_CATEGORY_QUERY_IDS = new Set(CATEGORY_FILTERS.map((c) => c.id).filter(Boolean));

/** Last opened activity from Explore (+ scroll fallback); scroll that card into view on Back. */
const EXPLORE_FEED_RETURN_KEY = 'playfynd_explore_feed_return';
const LEGACY_EXPLORE_FEED_SCROLL_KEY = 'playfynd_explore_feed_scroll_y';

const exploreActivityAnchorId = (activityId) => `explore-activity-${String(activityId)}`;

const FeedPage = () => {
  const navigationType = useNavigationType();
  const routerLocation = useRouterLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [verifyModal, setVerifyModal] = useState(false);
  const [joinLoginModal, setJoinLoginModal] = useState(false);
  const [joinLoginActivityId, setJoinLoginActivityId] = useState(null);
  const pendingAutoJoinAttemptedRef = useRef(false);
  const [exploreFilters, setExploreFilters] = useState(() => loadPersistedExploreFilters());
  const [exploreSort, setExploreSort] = useState(() => loadPersistedExploreSort());
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [sortSheetOpen, setSortSheetOpen] = useState(false);
  const { user } = useAuth();
  const { location: userLocation } = useLocation();

  const activityApiFilters = {
    includePast: true,
    ...(category ? { category } : {}),
    ...exploreFiltersToApiParams(exploreFilters),
  };

  const {
    activities,
    loading,
    refetch,
    newItemIds,
    prependVersion,
    scrollSnapshotRef,
  } = useActivities(activityApiFilters);

  useEffect(() => {
    persistExploreFilters(exploreFilters);
  }, [exploreFilters]);

  useEffect(() => {
    persistExploreSort(exploreSort);
  }, [exploreSort]);

  useNearbyNotifications();

  useEffect(() => {
    pendingAutoJoinAttemptedRef.current = false;
  }, [routerLocation.pathname, routerLocation.search, routerLocation.hash, user?._id]);

  /** After login/register, complete join started as a guest on this feed URL. */
  useEffect(() => {
    if (!user || loading) return;
    if (pendingAutoJoinAttemptedRef.current) return;
    const pendingJoin = readPendingJoinIntent();
    if (!pendingJoin?.activityId) return;
    const here = `${routerLocation.pathname}${routerLocation.search}${routerLocation.hash}`;
    if (pendingJoin.path !== here) return;

    pendingAutoJoinAttemptedRef.current = true;
    const activityId = String(pendingJoin.activityId);
    const act = activities.find((a) => String(a._id) === activityId);

    const run = async () => {
      if (act) {
        const alreadyParticipant = act.participants?.some((p) => String(p._id || p) === String(user._id));
        if (alreadyParticipant || act.status === 'cancelled' || isActivityListingEnded(act, new Date())) {
          clearPendingJoinIntent();
          return;
        }
        if (act.verifiedOnly && !user.faceVerified) {
          setVerifyModal(true);
          return;
        }
      }

      try {
        await api.post(`/activities/${activityId}/join`);
        trackActivityJoin({ activityId, category: act?.category });
        clearPendingJoinIntent();
        toast.success("You're in!");
        refetch();
      } catch (err) {
        const code = err.response?.data?.code;
        const msg = err.response?.data?.message || 'Failed to join';
        if (code === 'FACE_VERIFICATION_REQUIRED' || code === 'VERIFIED_ONLY_ACTIVITY') {
          setVerifyModal(true);
          return;
        }
        clearPendingJoinIntent();
        toast.error(msg);
      }
    };

    run();
  }, [
    user,
    loading,
    activities,
    refetch,
    routerLocation.pathname,
    routerLocation.search,
    routerLocation.hash,
  ]);

  const captureExploreReturnTarget = useCallback((activityId) => {
    if (activityId == null || activityId === '') return;
    try {
      sessionStorage.setItem(
        EXPLORE_FEED_RETURN_KEY,
        JSON.stringify({ activityId: String(activityId), scrollY: window.scrollY }),
      );
    } catch {
      /* ignore quota / private mode */
    }
  }, []);

  useLayoutEffect(() => {
    if (navigationType !== 'PUSH' && navigationType !== 'REPLACE') return;
    try {
      sessionStorage.removeItem(EXPLORE_FEED_RETURN_KEY);
      sessionStorage.removeItem(LEGACY_EXPLORE_FEED_SCROLL_KEY);
    } catch {
      /* ignore */
    }
  }, [navigationType]);

  useEffect(() => {
    const c = searchParams.get('category') || '';
    const q = searchParams.get('q') ?? '';
    setCategory(FEED_CATEGORY_QUERY_IDS.has(c) ? c : '');
    setSearch(q);
  }, [searchParams]);

  const lastPrependVersion = useRef(0);
  useLayoutEffect(() => {
    if (prependVersion === 0 || prependVersion === lastPrependVersion.current) return;
    lastPrependVersion.current = prependVersion;

    const snapshot = scrollSnapshotRef.current;
    scrollSnapshotRef.current = null;
    if (!snapshot || snapshot.scrollTop < 50) return;

    const newScrollHeight = document.documentElement.scrollHeight;
    const diff = newScrollHeight - snapshot.scrollHeight;
    if (diff > 0) {
      window.scrollTo({ top: window.scrollY + diff, behavior: 'auto' });
    }
  }, [prependVersion, scrollSnapshotRef]);

  const userLatLng =
    userLocation?.lat != null && userLocation?.lng != null
      ? { lat: userLocation.lat, lng: userLocation.lng }
      : null;

  const filtered = useMemo(
    () =>
      activities.filter((a) => {
        if (!a.title.toLowerCase().includes(search.toLowerCase())) return false;
        const creatorId = a.creator?._id || a.creator;
        if (user && creatorId && String(creatorId) === String(user._id)) return false;
        if (a._explorePast) return true;
        if (!matchesExploreFilters(a, exploreFilters, userLatLng)) return false;
        return true;
      }),
    [activities, exploreFilters, userLatLng, user, search],
  );

  /** Landing / shared links use `?category=&q=`; if that narrows to zero activities, widen to the full feed. */
  useEffect(() => {
    if (loading) return;
    const urlCat = searchParams.get('category') || '';
    const urlQ = (searchParams.get('q') ?? '').trim();
    if (!urlCat && !urlQ) return;
    if (filtered.length > 0) return;
    setCategory('');
    setSearch('');
    setSearchParams({}, { replace: true });
    toast('No matches for that topic — showing all vibes nearby.', { id: 'feed-deeplink-relax' });
  }, [loading, searchParams, filtered.length, setSearchParams]);

  const exploreFilterCount = countActiveExploreFilters(exploreFilters);

  const sortedUpcoming = useMemo(
    () => sortExploreActivities(
      filtered.filter((a) => !a._explorePast),
      exploreSort,
      userLatLng,
    ),
    [filtered, exploreSort, userLatLng],
  );

  const sortedPast = useMemo(
    () => sortExploreActivities(
      filtered.filter((a) => a._explorePast),
      exploreSort,
      userLatLng,
    ),
    [filtered, exploreSort, userLatLng],
  );

  useLayoutEffect(() => {
    if (navigationType !== 'POP') return;
    if (loading) return;

    let payload = null;
    try {
      const json = sessionStorage.getItem(EXPLORE_FEED_RETURN_KEY);
      if (json) {
        payload = JSON.parse(json);
      } else {
        const legacy = sessionStorage.getItem(LEGACY_EXPLORE_FEED_SCROLL_KEY);
        if (legacy != null) {
          const y = Number(legacy);
          if (Number.isFinite(y) && y >= 0) payload = { activityId: null, scrollY: y };
        }
      }
    } catch {
      try {
        sessionStorage.removeItem(EXPLORE_FEED_RETURN_KEY);
        sessionStorage.removeItem(LEGACY_EXPLORE_FEED_SCROLL_KEY);
      } catch {
        /* ignore */
      }
      return;
    }

    if (!payload || (payload.activityId == null && !Number.isFinite(Number(payload.scrollY)))) {
      try {
        sessionStorage.removeItem(EXPLORE_FEED_RETURN_KEY);
        sessionStorage.removeItem(LEGACY_EXPLORE_FEED_SCROLL_KEY);
      } catch {
        /* ignore */
      }
      return;
    }

    const activityId = payload.activityId != null ? String(payload.activityId) : '';
    const fallbackY = Number(payload.scrollY);

    const finish = () => {
      try {
        sessionStorage.removeItem(EXPLORE_FEED_RETURN_KEY);
        sessionStorage.removeItem(LEGACY_EXPLORE_FEED_SCROLL_KEY);
      } catch {
        /* ignore */
      }
    };

    const scrollToFallback = () => {
      if (!Number.isFinite(fallbackY) || fallbackY < 0) return;
      window.scrollTo({ top: fallbackY, left: 0, behavior: 'auto' });
      const max = Math.max(
        0,
        (document.documentElement?.scrollHeight ?? 0) - window.innerHeight,
      );
      if (window.scrollY > max) {
        window.scrollTo({ top: max, left: 0, behavior: 'auto' });
      }
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = activityId ? document.getElementById(exploreActivityAnchorId(activityId)) : null;
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'auto', inline: 'nearest' });
        } else {
          scrollToFallback();
        }
        finish();
      });
    });
  }, [navigationType, loading, filtered.length, sortedUpcoming.length, sortedPast.length]);

  const handleJoin = useCallback(async (activityId, { verifiedOnly, category } = {}) => {
    if (!user) {
      const path = `${routerLocation.pathname}${routerLocation.search}${routerLocation.hash}`;
      setPendingJoinIntent({ activityId, path });
      setJoinLoginActivityId(String(activityId));
      setJoinLoginModal(true);
      return;
    }
    if (verifiedOnly && !user.faceVerified) {
      setVerifyModal(true);
      return;
    }
    try {
      await api.post(`/activities/${activityId}/join`);
      trackActivityJoin({ activityId, category });
      toast.success("You're in!");
      refetch();
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === 'FACE_VERIFICATION_REQUIRED' || code === 'VERIFIED_ONLY_ACTIVITY') {
        setVerifyModal(true);
      }
      toast.error(err.response?.data?.message || 'Failed to join');
    }
  }, [user, refetch, routerLocation.pathname, routerLocation.search, routerLocation.hash]);

  return (
    <div className="min-h-screen w-full bg-surface-50 dark:bg-surface-950">
      <Header />

      <div className="w-full max-w-6xl mx-auto px-3 sm:px-6 lg:px-8 pb-24 lg:pb-10 pt-3 sm:pt-5">
        <h1 className="mb-3 text-xl font-extrabold tracking-tight text-surface-900 dark:text-white sm:text-2xl">
          Vibes Near You
        </h1>
        {/* Top filters */}
        <div className="mb-3 sm:mb-4">
          {/* Row 1: Location */}
          <div className="w-full pb-2.5 sm:pb-3">
            <LocationPicker />
          </div>

          {/* Row 2: Search + CTA */}
          <div className="sm:mt-3 flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
              <input
                type="text"
                placeholder="Search vibes"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-xl border border-surface-200/80 bg-white py-2 pl-9 pr-3 text-sm text-surface-800 placeholder:text-surface-400 transition-all focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:bg-surface-800/60 dark:border-surface-700/50 dark:text-white dark:placeholder:text-surface-500"
              />
            </div>
            <PostIntentButton />
          </div>
        </div>

        <div className="mb-3 sm:mb-4">
          <ExploreFilterBar
            filterCount={exploreFilterCount}
            onOpenSheet={() => {
              setSortSheetOpen(false);
              setFilterSheetOpen(true);
            }}
            onOpenSort={() => {
              setFilterSheetOpen(false);
              setSortSheetOpen(true);
            }}
          />
        </div>

        {/* People looking */}
        <PeopleLooking />

        {/* Category filters */}
        <div className="mb-4 flex gap-1.5 overflow-x-auto scrollbar-hide pb-0.5 -mx-3 px-3 sm:-mx-6 sm:px-6 lg:mx-0 lg:flex-wrap lg:justify-center lg:overflow-x-visible lg:px-0">
          {CATEGORY_FILTERS.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setCategory(cat.id)}
              className={`shrink-0 flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${
                category === cat.id
                  ? 'bg-gradient-to-r from-brand-500 to-brand-600 text-white shadow-sm shadow-brand-500/25'
                  : 'bg-white text-surface-600 border border-surface-200/80 hover:border-brand-200 dark:bg-surface-800/50 dark:text-surface-300 dark:border-surface-700/50'
              }`}
            >
              <span className="text-sm">{cat.emoji}</span>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Activity list / grid */}
        {loading ? (
          <Spinner className="py-16" />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Search}
            title="No vibes here yet. Start one."
            description={
              exploreFilterCount > 0
                ? 'Loosen filters or pick another spot — then start a vibe.'
                : 'Change your location or category, or start a new vibe for yaars nearby.'
            }
          />
        ) : (
          <div className="grid min-w-0 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {sortedUpcoming.map((activity) => (
              <div
                key={activity._id}
                id={exploreActivityAnchorId(activity._id)}
                className={`min-w-0 scroll-mt-24 sm:scroll-mt-28 ${newItemIds.has(activity._id) ? 'animate-silent-enter' : ''}`}
              >
                <ActivityCard
                  activity={activity}
                  onJoin={handleJoin}
                  onBeforeNavigateToActivity={captureExploreReturnTarget}
                />
              </div>
            ))}
            {sortedPast.length > 0 && (
              <h2
                className={`col-span-full text-xs font-bold tracking-wider text-surface-500 dark:text-surface-400 ${
                  sortedUpcoming.length > 0 ? 'mt-8' : ''
                }`}
              >
                Past Vibes on Yaarvi
              </h2>
            )}
            {sortedPast.map((activity) => (
              <div
                key={activity._id}
                id={exploreActivityAnchorId(activity._id)}
                className="min-w-0 scroll-mt-24 sm:scroll-mt-28"
              >
                <ActivityCard
                  activity={activity}
                  explorePast
                  onBeforeNavigateToActivity={captureExploreReturnTarget}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <FaceVerificationModal open={verifyModal} onClose={() => setVerifyModal(false)} variant="join" />

      <JoinLoginPromptModal
        open={joinLoginModal}
        onClose={() => {
          setJoinLoginModal(false);
          setJoinLoginActivityId(null);
        }}
        activityId={joinLoginActivityId}
      />

      <ExploreFilterSheet
        open={filterSheetOpen}
        appliedFilters={exploreFilters}
        onClose={() => setFilterSheetOpen(false)}
        onApply={setExploreFilters}
        onResetAll={() => setExploreFilters(cloneExploreFilters({}))}
      />

      <ExploreSortSheet
        open={sortSheetOpen}
        selectedSort={exploreSort}
        onClose={() => setSortSheetOpen(false)}
        onSelectSort={(id) => {
          setExploreSort(id);
          setSortSheetOpen(false);
        }}
      />
    </div>
  );
};

export default FeedPage;
